/* eslint-disable @typescript-eslint/no-explicit-any */
import env from 'env'

import drm from '../config.drm.stage.json'

import { VendorVideoPlayer, Config, PlayerSrc } from '..'

import type { PlayerEvent } from 'bitmovin-player/types/core/Events'
import type { PlayerConfig, PlayerAPI, SourceConfig } from 'bitmovin-player'

import logger from '../../../utils/logger'

import {
  VIDEO_PLAYBACK_ENDED,
  VIDEO_PLAYBACK_ERROR,
  VIDEO_PLAYBACK_PAUSE,
  VIDEO_PLAYBACK_SEEK,
  VIDEO_PLAYBACK_START,
  VIDEO_PLAYBACK_TIME,
  VIDEO_PLAYER_MOUNTED,
  VIDEO_PLAYER_UNMOUNTED
} from '../../../events/types/video.events'

import { EVENT_PLAYBACK_STATUS } from '../../../constants/enums'
import { load } from '../../../utils'

declare global {
  interface Window {
    bitmovin: any
  }
}

const CUSTOM_CSS = `${ env.NUGSNET_CDN}/bitmovin/styles/nugs.css`

const {
  BITMOVIN_KEY
} = env

export type BitmovinConfig = {
  player: PlayerConfig,
  source: SourceConfig
}

export interface BitmovinLibrary {
  player: any,
  playerui: any
}

export class NugsBitmovinPlayer extends VendorVideoPlayer<PlayerSrc> {
  bitmovin!: BitmovinLibrary
  #player!: PlayerAPI
  #config: BitmovinConfig
  #isInitialized = false

  get player() {
    return this.#player
  }

  get isFullscreen() {
    return this.player.getViewMode() === 'fullscreen'
  }

  get currentTime() {
    return this.player?.getCurrentTime() ?? 0
  }

  get options() {
    return this.#config
  }

  set src(value: PlayerSrc) {
    this.options.source = typeof value === 'string' ?
      { ...this.options.source, hls: value } :
      { ...this.options.source, ...value }
  }

  get src() {
    return {
      dash: this.options.source.dash || '',
      hls: this.options.source.hls || ''
    }
  }

  static get name() {
    return 'NugsBitmovinPlayer'
  }

  public get element() {
    return this.player.getVideoElement()
  }

  constructor(config: Config, userId = '') {
    super(config, userId)

    const headers = {
      'x-dt-custom-data': window.btoa(JSON.stringify({
        merchant: drm?.merchant,
        userId: userId,
        sessionId: config.drm?.sessionId || 'default'
      }))
    }

    this.#config = {
      player: {
        key: BITMOVIN_KEY,
        analytics: {
          customUserId: userId,
        },
        playback: {
          autoplay: this.config.autoplay,
        },
        remotecontrol: {
          type: 'googlecast',
          receiverApplicationId: env.CHROMECAST_APPLICATION_ID ?? 'FFE417E5', // Bitmovin default receiver application
          receiverVersion: 'v3',
        },
        ui: {
          playbackSpeedSelectionEnabled: false
        },
        location: {
          ui: 'https://cdn.bitmovin.com/player/web/8/bitmovinplayer-ui.js',
          ui_css: 'https://cdn.bitmovin.com/player/web/8/bitmovinplayer-ui.css',
        },
        logs: {
          bitmovin: false,
        }
      },
      source: {
        options: {
          withCredentials: true,
          manifestWithCredentials: true,
        },
      }
    } as BitmovinConfig

    if (this.config?.drm?.sessionId) {
      this.#config.source = {
        drm: {
          widevine: {
            LA_URL: drm.widevine.server,
            headers: {
              ...headers,
              'Content-Type': 'application/octet-stream',
            },
          },
          playready: {
            LA_URL: drm.playready.server,
            headers: {
              ...headers,
              'Content-Type': 'text/xml',
            },
          },
          fairplay: {
            LA_URL: drm.fairplay.server,
            certificateURL: drm.fairplay.cert,
            headers,
            useUint16InitData: true,
            prepareMessage: (event: any) => {
              return 'spc=' + encodeURIComponent(event.messageBase64Encoded)
            },
            prepareContentId: (contentId: any) => {
              return contentId
            },
          },
        }
      }
    }
  }

  async load<StaticPlayerAPI>() {
    if (this.bitmovin) return this.bitmovin as StaticPlayerAPI
    return await load<StaticPlayerAPI>(`https://cdn.bitmovin.com/player/web/8/bitmovinplayer.js`, 'bitmovin')
  }

  public async mount(el?: HTMLElement) {
    this.bitmovin = Object.assign({}, window.bitmovin)

    this.#player = new this.bitmovin.player.Player(el, this.options.player)

    await Promise.allSettled([
      load(CUSTOM_CSS, undefined, 'text/css'),
      this.player.load(this.options.source)
    ])

    this.bitmovin.playerui = { ...window.bitmovin.playerui }

    this.loadUI()
  }

  public async init() {
    if (this.#isInitialized || !this.bitmovin) return
    this.#isInitialized = true

    this.player.on('ready' as PlayerEvent, () => {
      this.video = this.element
      this.events.dispatchEvent(VIDEO_PLAYER_MOUNTED)
    })

    this.player.on('play' as PlayerEvent, () => this.events.dispatchEvent(VIDEO_PLAYBACK_START))
    this.player.on('paused' as PlayerEvent, () => this.events.dispatchEvent(VIDEO_PLAYBACK_PAUSE))

    this.player.on('seek' as PlayerEvent, event => {
      const position = event['position' as never]
      const seekTarget = event['seekTarget' as never]

      this.chapters.track(position, seekTarget, EVENT_PLAYBACK_STATUS.SKIPPED)
      this.events.dispatchEvent(VIDEO_PLAYBACK_SEEK(position, seekTarget))
    })

    this.player.on('playbackfinished' as PlayerEvent, () => this.events.dispatchEvent(VIDEO_PLAYBACK_ENDED))

    this.player.on('timechanged' as PlayerEvent, event => {
      const position = event['time' as never]
      const duration = this.player.getDuration()
      const element = this.player.getVideoElement() as HTMLVideoElement

      this.chapters.track(position, position, EVENT_PLAYBACK_STATUS.COMPLETED)
      this.events.dispatchEvent(VIDEO_PLAYBACK_TIME(
        position,
        element.played,
        duration
      ))
    })

    this.player.on('error' as PlayerEvent, () => this.events.dispatchEvent(VIDEO_PLAYBACK_ERROR))

    const player = this.player
    this.player.on('subtitleadded' as PlayerEvent, function (event: any) {
      player.subtitles?.remove(event?.subtitle?.id)
    })

    logger.BLACK.FILL(NugsBitmovinPlayer.name, 'init', this.bitmovin.player)
  }

  private loadUI() {
    const myUiManager = this.bitmovin.playerui?.UIFactory?.buildDefaultUI(this.player, this.options.player.ui)

    this.chapters.data.map(marker => {
      myUiManager?.addTimelineMarker({
        time: marker.marker,
        title: marker.title
      })
    })
  }

  public pause(): void {
    this.player?.pause()
  }

  public stop(): void {
    this.player?.pause()
  }

  public play(): void {
    this.player?.play()
  }

  public seek(time: number, issuer?: string): void {
    this.player?.seek(time, issuer)
  }

  public unmount() {
    this.events.dispatchEvent(VIDEO_PLAYER_UNMOUNTED)
    this.player?.destroy()
    return Promise.resolve()
  }
}
